// creates workflow action component depending on action.type and injects action-specific properties from store

import moment from "moment";
import React, { Component } from "react";
import LabeledAction from "./LabeledAction";
import { ActionText, ActionTextCollapsed } from "./ActionText/ActionText";
import { ActionDate, ActionDateCollapsed } from "./ActionDate/ActionDate";
import {
  ActionTranslationMatrix,
  ActionTranslationMatrixCollapsed,
} from "./ActionTranslationMatrix/ActionTranslationMatrix";
import ActionSingleFile from "./ActionSingleFile/ActionSingleFile";
import ActionSingleFileCollapsed from "./ActionSingleFile/ActionSingleFileCollapsed";
import ActionUploadFileset from "./ActionUploadFileset";
import ActionUploadFilesetCollapsed from "./ActionUploadFileset/collapsed";
import ActionSelectFromListOrInputValue from "./ActionSelectFromListOrInputValue/ActionSelectFromListOrInputValue";
import ActionConfirm from "./ActionConfirm/ActionConfirm";
import ActionGeoJson from "./ActionGeoJson";
import ActionGeoJsonCollapsed from "./ActionGeoJson/collapsed";

import { connect } from "react-redux";
import {
  getCurrentCustomerId,
  getCurrentSiteId,
} from "../../../../redux/modules/route/routes";
import { guid } from "../../../../utils";

import type { WorkflowAction } from "../../../../redux/modules/workflow/reducer";
import type { ActionProps } from "./_ActionProps";
import Alert from "../../../components/Alert";
import { getCurrentSiteState } from "../../../../redux/modules/rootReducer";
import { CurrentSiteState } from "../../../../redux/modules/currentSite/reducer";

interface Props {
  action: WorkflowAction;
  customerId: string;
  siteId?: string;
  currentSite: CurrentSiteState;
  onInputSuccess: (value: any) => void;
  onInputFailure: (value: any) => void;
  disabled?: boolean;
  collapsed?: boolean;
}

// FIXME fix action component prop types
class WorkflowActionContainer extends Component<Props> {
  render() {
    const { action, collapsed } = this.props;

    if (collapsed) {
      return this.renderCollapsedAction();
    }

    return (
      <LabeledAction id={action.id} label={action.description}>
        {this.renderFullAction()}
      </LabeledAction>
    );
  }

  renderFullAction() {
    const {
      action,
      customerId,
      siteId,
      currentSite,
      disabled,
      onInputSuccess,
      onInputFailure,
    } = this.props;

    const commonActionProps: ActionProps<any> = {
      value: action.value,
      label: action.label,
      disabled,
      onInputSuccess,
      onInputFailure,
    };

    switch (action.type) {
      case "TEXT": {
        const textProps = {
          ...commonActionProps,
          regexp: action.regexp,
        };

        return <ActionText {...textProps} />;
      }
      case "DATE": {
        const dateProps = {
          ...commonActionProps,
          defaultValue: action.defaultToday
            ? moment().format("YYYY-MM-DD")
            : "",
        };

        return <ActionDate {...dateProps} />;
      }
      case "SINGLE_FILE": {
        if (!siteId) {
          return (
            <Alert>Cant render SINGLE_FILE action, site context required</Alert>
          );
        }

        const singleFileProps = {
          ...commonActionProps,
          uploadPath: guid(),
          customerId,
          siteId,
        };

        return <ActionSingleFile {...singleFileProps} />;
      }
      case "UPLOAD_FILESET": {
        const uploadFilesetProps = {
          ...commonActionProps,
          uploadPath: guid(),
          customerId,
          siteId,
        };

        // @ts-ignore
        return <ActionUploadFileset {...uploadFilesetProps} />;
      }
      case "SELECT_FROM_LIST":
      case "SELECT_FROM_LIST_OR_INPUT_VALUE": {
        if (!action.options) {
          return <pre>failed to render SELECT action; options not found</pre>;
        }
        const selectFromListOrInputValueProps = {
          ...commonActionProps,
          options: action.options,
        };

        return (
          // @ts-ignore
          <ActionSelectFromListOrInputValue
            {...selectFromListOrInputValueProps}
          />
        );
      }
      case "GEOJSON":
        return <ActionGeoJson {...commonActionProps} />;
      case "TRANSLATION_MATRIX":
        return <ActionTranslationMatrix {...commonActionProps} />;
      case "CONFIRM":
        return <ActionConfirm {...commonActionProps} />;
      default:
        return <pre>{`${action.id} - ${action.type}`}</pre>;
    }
  }

  renderCollapsedAction() {
    const { action } = this.props;

    const collapsedActionProps = {
      value: action.value,
      label: action.description || action.label,
    };

    if (action.id == "site-epsg") {
      // Don't show EPSG code as it may be changed in CRS form and confuse user
      return null;

      // Translation matrix also may confuse them, but we use collapsed version
      // to show instructions to go to CRS form.
    }

    switch (action.type) {
      case "TEXT":
      case "DATE":
        return <ActionDateCollapsed {...collapsedActionProps} />;
      case "SINGLE_FILE":
        return <ActionSingleFileCollapsed {...collapsedActionProps} />;
      case "UPLOAD_FILESET":
        return (
          <ActionUploadFilesetCollapsed
            label={action.label}
            value={action.value}
          />
        );
      case "SELECT_FROM_LIST":
      case "SELECT_FROM_LIST_OR_INPUT_VALUE":
        if (!action.options) {
          return <pre>failed to render SELECT action; options not found</pre>;
        }

        return (
          <ActionTextCollapsed
            label={action.label}
            value={action.options[action.value]}
          />
        );
      case "GEOJSON":
        return <ActionGeoJsonCollapsed value={action.value} />;
      case "TRANSLATION_MATRIX":
        return (
          <ActionTranslationMatrixCollapsed
            customerId={this.props.customerId}
            siteId={this.props.siteId}
          />
        );
      case "CONFIRM":
        // is not shown in collapsed state
        return null;
      default:
        return <pre>{`${action.id} - ${action.type}`}</pre>;
    }
  }
}

export default connect((state, ownProps) => {
  const currentCustomerId = getCurrentCustomerId(state.route);
  const currentSiteId = getCurrentSiteId(state.route);

  return {
    ...ownProps,
    customerId: currentCustomerId,
    siteId: currentSiteId,
    currentSite: getCurrentSiteState(state),
  };
})(WorkflowActionContainer);
